import { useMemo } from "react";
import { useOrderStore } from "../contexts/LTPStore/useOrdersStore";
import { useLtpStore } from "../contexts/LTPStore/useLTPStore";
import { usePositionStore } from "../contexts/LTPStore/usePositionsStore";
import { useGlobalData } from "./useGlobalHooks";

/**
 * Hook to identify trades nearing their trigger price
 * @param {number} threshold - Rupee gap to trigger
 * @param {string} mode - 'orders' (Entry Buy) or 'positions' (SL Sell) or 'both'
 * @param {Object} symbolStrategyBaseQty - Map of base quantities for 1x multiplier
 */
export function usePotentialTrades(threshold = 50, mode = "both", symbolStrategyBaseQty = {}) {
  const orders = useOrderStore((state) => state.orders) || [];
  const ltps = useLtpStore((state) => state.ltps) || {};
  const positions = usePositionStore((state) => state.positions) || [];
  const { users } = useGlobalData();

  const groupedTrades = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    const groups = {};

    orders.forEach((order) => {
      const status = (order.status || "").toLowerCase();
      const statusNo = parseInt(order.status_no, 10);
      const isPending =
        status === "pending" ||
        status === "open" ||
        status === "trigger pending" ||
        status === "not sent" ||
        statusNo === 6 ||
        statusNo === 4;

      if (!isPending) return;

      const symbol = order.symbol || "";
      if (!symbol) return;
      
      const symbolUpper = symbol.toUpperCase();
      let nudgeCategory = "O";
      const isFut = symbolUpper.endsWith("FUT");
      const isMcx = symbolUpper.includes("MCX");
      
      if (isFut || isMcx) {
         nudgeCategory = "P";
         const strategy = order.strategy || "N/A";
         if (strategy === "N/A" || strategy.trim() === "") return;
      } else if (symbolUpper.includes("NFO") || symbolUpper.includes("BFO")) {
         return;
      }

      const ltp = ltps[symbol];
      if (ltp === undefined || ltp === null) return;

      const sideStr = String(order.side).toUpperCase();
      const isBuy = sideStr === "1" || sideStr === "BUY";
      const isSell = sideStr === "-1" || sideStr === "2" || sideStr === "SELL";

      // Filter based on mode
      if (mode === "orders") {
        if (!isBuy) return;
      } else if (mode === "positions") {
        if (!isSell) return;
        
        // Map against active positions
        const oClientId = (order.clientId || order.client_id || "").toString();
        const hasMatchingPosition = positions?.some(p => {
          const pClientId = (p.clientId || p.client_id || "").toString();
          const netQty = Math.abs(Number(p.net_qty ?? p.qty ?? 0));
          return pClientId === oClientId && p.symbol === order.symbol && netQty > 0;
        });
        
        if (!hasMatchingPosition) return;
      }

      const limitPrice = Number(order.limitPrice || order.limit_price || 0);
      const stopPrice = Number(order.stopPrice || order.stop_price || 0);

      // Priority logic based on order side
      let comparePrice = 0;
      if (isSell) {
        comparePrice = stopPrice > 0 ? stopPrice : limitPrice;
      } else if (isBuy) {
        comparePrice = limitPrice > 0 ? limitPrice : stopPrice;
      }

      if (comparePrice > 0) {
        const diff = Math.abs(ltp - comparePrice);
        if (diff <= threshold) {
          // KEY: Unique based on symbol, side, strategy, limitPrice, and stopPrice
          // Irrespective of client_id
          const sideLabel = isBuy ? "BUY" : "SELL";
          const strategy = order.strategy || "N/A";
          const key = `${symbol}_${sideLabel}_${strategy}_${limitPrice}_${stopPrice}`;

          if (!groups[key]) {
            // Look up base qty for the nudge
            const lookupKey = `${symbol}_${strategy === "N/A" ? "NO_STRATEGY" : strategy}`;
            let baseQty = "N/A";
            if (symbolStrategyBaseQty && symbolStrategyBaseQty[lookupKey]) {
              baseQty = symbolStrategyBaseQty[lookupKey].baseQty;
            }

            groups[key] = {
              id: key,
              symbol,
              strategy,
              sideLabel,
              limitPrice,
              stopPrice,
              comparePrice,
              ltp,
              diff,
              type: isBuy ? "Entry Order" : "Exit/SL",
              totalQty: 0,
              clientCount: 0,
              baseQty: baseQty,
              clients: [],
              nudgeCategory: nudgeCategory
            };
          }

          const qty = Number(order.qty || 0);
          groups[key].totalQty += qty;
          groups[key].clientCount += 1;
          
          const clientId = order.clientId || order.client_id;
          if (!groups[key].clients.includes(clientId)) {
              groups[key].clients.push(clientId);
          }
        }
      }
    });

    return Object.values(groups).sort((a, b) => a.diff - b.diff);
  }, [orders, ltps, users, positions, mode, threshold, symbolStrategyBaseQty]);

  return { 
    potentialTrades: groupedTrades, 
    groupedNudges: groupedTrades 
  };
}
